import { getBasePath } from './ws-url';
import { guobiaoConfigForSeat } from './guobiao-preferences';

export type UserPublic = {
  id: string;
  nickname: string;
  avatarIndex: number;
  avatarUrl?: string | null;
  matchPoints: number;
  bgmEnabled: boolean;
  sfxEnabled: boolean;
  createdAt: number;
  updatedAt: number;
};

export type MatchPointsInfo = {
  matchPoints: number;
  canClaimDaily: boolean;
  dailyClaimAmount: number;
  todayKey: string;
  lastClaimDay: string | null;
};

export type ActiveGameInfo = {
  gameId: string;
  seat: number;
  roomType: string | null;
  gameType?: string | null;
};

export type TrusteeStatusInfo = {
  gameId: string;
  seat: number;
  enabled: boolean;
  offlineSince: number | null;
};

type EnsureUserResult = { created: boolean; loginToken: string; apiToken: string | null; user: UserPublic };
type EnsureUserOptions = { strictExistingToken?: boolean };

const LOGIN_TOKEN_KEY = 'mjlab_login_token';
const LEGACY_LOGIN_TOKEN_KEY = 'autotable_login_token';
let ensureUserInFlightDefault: Promise<EnsureUserResult> | null = null;
let ensureUserInFlightStrict: Promise<EnsureUserResult> | null = null;

export function getLoginToken(): string | null {
  try {
    const current = localStorage.getItem(LOGIN_TOKEN_KEY);
    if (current) return current;
    const legacy = localStorage.getItem(LEGACY_LOGIN_TOKEN_KEY);
    if (legacy) {
      localStorage.setItem(LOGIN_TOKEN_KEY, legacy);
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

export function setLoginToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(LOGIN_TOKEN_KEY, token);
    else localStorage.removeItem(LOGIN_TOKEN_KEY);
    // Keep legacy key cleared to avoid confusion.
    if (!token) localStorage.removeItem(LEGACY_LOGIN_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function getApiBaseUrl(): string {
  // @ts-ignore
  const env = process.env.NODE_ENV;
  if (env !== 'production') {
    return 'http://localhost:1235/api';
  }
  const basePath = getBasePath();
  return `${window.location.origin}${basePath}api`;
}

export async function createAnonymousUser(): Promise<{ loginToken: string; apiToken: string; user: UserPublic }> {
  return apiJson('/auth/anonymous', { method: 'POST' });
}

export async function fetchMe(): Promise<{ user: UserPublic }> {
  return apiJson('/me', { method: 'GET', auth: true });
}

export async function updateMe(
  patch: { nickname?: string; avatarIndex?: number; avatarUrl?: string | null; bgmEnabled?: boolean; sfxEnabled?: boolean }
): Promise<{ user: UserPublic }> {
  return apiJson('/me', { method: 'PATCH', auth: true, body: patch });
}

export async function uploadMeAvatar(dataUrl: string): Promise<{ user: UserPublic; avatarUrl: string }> {
  return apiJson('/me/avatar', { method: 'POST', auth: true, body: { dataUrl } });
}

export async function resetApiToken(): Promise<{ apiToken: string; user: UserPublic }> {
  return apiJson('/me/reset-api-token', { method: 'POST', auth: true });
}

export async function resetLoginToken(): Promise<{ loginToken: string; user: UserPublic }> {
  return apiJson('/me/reset-login-token', { method: 'POST', auth: true });
}

export async function newbieStart(options: { chatgptHand?: boolean } = {}): Promise<{ gameId: string; reused: boolean }> {
  return apiJson('/newbie/start', { method: 'POST', auth: true, body: options.chatgptHand ? { chatgptHand: true } : undefined });
}

export async function newbieNew(): Promise<{ gameId: string }> {
  return apiJson('/newbie/new', { method: 'POST', auth: true });
}

export async function guobiaoNewbieStart(options: { chatgptHand?: boolean } = {}): Promise<{ gameId: string; reused: boolean }> {
  return apiJson('/guobiao/newbie/start', {
    method: 'POST',
    auth: true,
    body: { guobiaoConfig: guobiaoConfigForSeat(0), ...(options.chatgptHand ? { chatgptHand: true } : {}) },
  });
}

export async function guobiaoNewbieNew(): Promise<{ gameId: string }> {
  return apiJson('/guobiao/newbie/new', { method: 'POST', auth: true, body: { guobiaoConfig: guobiaoConfigForSeat(0) } });
}

export type ActiveChatgptGameInfo = {
  gameId: string;
  viewerSeat: number;
  playerId: string;
  expiresAt: string;
  updatedAt: string;
};

export async function bindActiveChatgptGame(gameId: string): Promise<{ activeChatgptGame: ActiveChatgptGameInfo }> {
  return apiJson('/chatgpt-hand/active', { method: 'POST', auth: true, body: { gameId } });
}

export async function createChatgptOAuthAuthorizeRedirect(params: Record<string, string>): Promise<{
  redirectUrl: string;
  scope: string;
  expiresAt: string;
}> {
  return apiJson('/chatgpt-oauth/authorize', { method: 'POST', auth: true, body: params });
}

export async function fetchActiveGame(): Promise<{ activeGame: ActiveGameInfo | null }> {
  return apiJson('/me/active-game', { method: 'GET', auth: true });
}

export async function abandonActiveGame(
  gameId?: string
): Promise<{ ok: true; abandoned: ActiveGameInfo | null; remainingActiveGame?: ActiveGameInfo | null }> {
  const normalized = typeof gameId === 'string' ? gameId.trim() : '';
  const body = normalized ? { gameId: normalized } : undefined;
  return apiJson('/me/active-game/abandon', { method: 'POST', auth: true, body });
}

export async function fetchTrusteeStatus(gameId: string): Promise<{ trustee: TrusteeStatusInfo | null }> {
  const normalized = String(gameId ?? '').trim();
  if (!normalized) {
    throw new Error('missing gameId');
  }
  return apiJson(`/me/trustee-status?gameId=${encodeURIComponent(normalized)}`, { method: 'GET', auth: true });
}

export async function disableTrustee(gameId: string): Promise<{ ok: true; trustee: TrusteeStatusInfo | null }> {
  const normalized = String(gameId ?? '').trim();
  if (!normalized) {
    throw new Error('missing gameId');
  }
  return apiJson('/me/trustee/disable', { method: 'POST', auth: true, body: { gameId: normalized } });
}

export async function ensureUser(options?: EnsureUserOptions): Promise<EnsureUserResult> {
  const strict = options?.strictExistingToken === true;
  const inFlight = strict ? ensureUserInFlightStrict : ensureUserInFlightDefault;
  if (inFlight) {
    return inFlight;
  }

  const loader = (async (): Promise<EnsureUserResult> => {
    const existing = getLoginToken();
    if (existing) {
      try {
        const { user } = await fetchMe();
        return { created: false, loginToken: existing, apiToken: null, user };
      } catch (err: unknown) {
        const msg = String((err as any)?.message ?? err);
        // Existing token is invalid.
        if (msg.toLowerCase().includes('unauthorized') || msg.startsWith('401')) {
          setLoginToken(null);
          if (strict) {
            throw new Error('unauthorized');
          }
        } else {
          throw err;
        }
      }
    }

    const { loginToken, apiToken, user } = await createAnonymousUser();
    setLoginToken(loginToken);
    return { created: true, loginToken, apiToken, user };
  })();
  if (strict) {
    ensureUserInFlightStrict = loader;
  } else {
    ensureUserInFlightDefault = loader;
  }

  try {
    return await loader;
  } finally {
    if (strict) {
      ensureUserInFlightStrict = null;
    } else {
      ensureUserInFlightDefault = null;
    }
  }
}

type ApiJsonOptions = {
  method: 'GET' | 'POST' | 'PATCH';
  auth?: boolean;
  authToken?: string;
  body?: any;
};

async function apiJson(path: string, options: ApiJsonOptions): Promise<any> {
  const url = `${getApiBaseUrl()}${path.startsWith('/') ? '' : '/'}${path}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const needsAuth = options.auth || typeof options.authToken === 'string';
  if (needsAuth) {
    const loginToken = options.authToken ?? getLoginToken();
    if (!loginToken) {
      throw new Error('missing login token');
    }
    headers['Authorization'] = `Bearer ${loginToken}`;
  }

  const resp = await fetch(url, {
    method: options.method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await resp.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!resp.ok) {
    const msg = json?.error ? String(json.error) : `${resp.status} ${resp.statusText}`;
    const error = new Error(msg) as Error & { status?: number; data?: any };
    error.status = resp.status;
    error.data = json;
    throw error;
  }

  return json;
}

export async function fetchMeWithToken(loginToken: string): Promise<{ user: UserPublic }> {
  return apiJson('/me', { method: 'GET', authToken: loginToken });
}

export async function fetchMatchPointsInfo(): Promise<MatchPointsInfo> {
  return apiJson('/me/match-points', { method: 'GET', auth: true });
}

export async function fetchMatchPointsInfoWithToken(loginToken: string): Promise<MatchPointsInfo> {
  return apiJson('/me/match-points', { method: 'GET', authToken: loginToken });
}

export async function claimDailyMatchPoints(): Promise<{
  ok: true;
  claimed: number;
  matchPoints: number;
  canClaimDaily: boolean;
  dailyClaimAmount: number;
  todayKey: string;
  lastClaimDay: string | null;
}> {
  return apiJson('/me/match-points/claim', { method: 'POST', auth: true });
}
